import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { IconButton } from "rsuite";
import PropTypes from "prop-types";
import classNames from "classnames";
import TabsContext from "./TabsContext";
import SquareLeft from "../../assets/img/square-left.svg?react";
import SquareRight from "../../assets/img/square-right.svg?react";
import "./tabs.scss";
import {
  getChildByIndex,
  isElementOutsideLeftEdge,
  isElementOutsideRightEdge,
  getChildrenAsArray,
} from "./tabUtils";

const TabList = ({ children, className, style = {} }) => {
  const tablistElementRef = useRef(null);
  const tablistContainerRef = useRef(null);
  const { focusedTab, setFocusedTab, activeTab } = useContext(TabsContext);
  const [tablistWidth, setTablistWidth] = useState(0);
  const [showPreviousButton, setShowPreviousButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const [scrollValue, setScrollValue] = useState(0);
  const [scrollIndex, setScrollIndex] = useState(activeTab || 0);
  const autoScrollInProgressRef = useRef(false);

  const childElements = getChildrenAsArray(children);

  /**
   * Pass the index as prop to each tab element
   */
  const tabs = childElements.map((child, index) => {
    if (React.isValidElement(child) && child.type.componentName === "Tab") {
      // eslint-disable-next-line react/no-array-index-key
      return React.cloneElement(child, { index, key: index });
    }
    return null;
  });

  /**
   * Calculate the scroll position
   */
  const updateScrollPosition = useCallback(() => {
    // Don't interfere if auto-scroll just happened
    if (autoScrollInProgressRef.current) {
      return;
    }

    // Get the tab element by the index
    const tabElement = getChildByIndex(scrollIndex, tablistElementRef.current);
    if (tabElement !== null) {
      // Calculate the distance of tab edges to the left edge of the tablist
      const tabRightEdgeDistance =
        tabElement.offsetLeft + tabElement.offsetWidth;
      const tabLeftEdgeDistance = tabElement.offsetLeft;
      // Calculate how much tab right edge is outside the tablist container
      const tabListContainerWidth = tablistContainerRef.current.offsetWidth;
      const tabRightEdgeOffset = tabRightEdgeDistance - tabListContainerWidth;
      // Check if tab right edge is outside the right edge of the container
      if (
        tabRightEdgeOffset > 0 &&
        (scrollValue < tabRightEdgeOffset ||
          scrollIndex === tablistElementRef.current.children.length - 1)
      ) {
        setScrollValue(tabRightEdgeOffset);
      }
      // Check if tab left edge is outside the left edge of the container
      else if (tabLeftEdgeDistance < scrollValue) {
        setScrollValue(tabLeftEdgeDistance);
      }
    }
  }, [scrollIndex, scrollValue]);

  /**
   * Find the next tab that is outside the right edge of the tablist container
   */
  const findNextElementOutsideRightEdge = useCallback(() => {
    const tabListElement = tablistElementRef.current;
    const tabIndex = Array.from(tabListElement.children).findIndex((element) =>
      isElementOutsideRightEdge(
        element,
        tablistContainerRef.current,
        scrollValue
      )
    );
    return tabIndex;
  }, [scrollValue]);

  /**
   * Find the next tab that is outside the left edge of the tablist
   */
  const findNextElementOutsideLeftEdge = useCallback(() => {
    const tabListElement = tablistElementRef.current;
    const tabIndex = Array.from(tabListElement.children)
      .reverse()
      .findIndex((element) => isElementOutsideLeftEdge(element, scrollValue));
    return tabIndex > -1 ? tabListElement.children.length - tabIndex - 1 : -1;
  }, [scrollValue]);

  /**
   * Hide/show scroll buttons
   */
  const updateScrollButtons = useCallback(() => {
    setShowPreviousButton(findNextElementOutsideLeftEdge() !== -1);
    setShowNextButton(findNextElementOutsideRightEdge() !== -1);
  }, [findNextElementOutsideLeftEdge, findNextElementOutsideRightEdge]);

  /**
   * Calculate dimensions for tablist and container
   */
  const calculateDimensions = useCallback(() => {
    // Callers reach here from a deferred rAF/setTimeout chain, so the refs may
    // have been detached by an unmount in between. Report zeroes rather than
    // dereferencing null — every caller already handles the all-tabs-fit case.
    if (!tablistElementRef.current || !tablistContainerRef.current) {
      return { totalWidth: 0, containerWidth: 0, maxScroll: 0 };
    }

    const children = Array.from(tablistElementRef.current.children);

    // Calculate total width including margins between tabs
    // offsetWidth doesn't include margins, so we need to add them
    const totalWidth = children.reduce((total, el, index) => {
      const elWidth = el.offsetWidth;
      // Add margin-right (1px) for all tabs except the last one
      const marginRight = index < children.length - 1 ? 1 : 0;
      return total + elWidth + marginRight;
    }, 0);

    const containerWidth = tablistContainerRef.current.offsetWidth;
    const maxScroll = Math.max(0, totalWidth - containerWidth);

    return { totalWidth, containerWidth, maxScroll };
  }, []);

  /**
   * Check if active tab is outside the current viewport
   */
  const isActiveTabOutsideViewport = useCallback(
    (containerWidth, currentScroll) => {
      const activeTabElement = getChildByIndex(
        activeTab,
        tablistElementRef.current
      );

      if (!activeTabElement) {
        return false;
      }

      const tabLeftEdge = activeTabElement.offsetLeft;
      const tabRightEdge = tabLeftEdge + activeTabElement.offsetWidth;
      const viewportLeftEdge = currentScroll;
      const viewportRightEdge = currentScroll + containerWidth;

      // Check if tab is cut off on either side
      return tabRightEdge > viewportRightEdge || tabLeftEdge < viewportLeftEdge;
    },
    [activeTab]
  );

  /**
   * Calculate scroll value needed to make active tab visible
   */
  const calculateScrollForActiveTab = useCallback(
    (containerWidth, maxScroll, currentScroll) => {
      const activeTabElement = getChildByIndex(
        activeTab,
        tablistElementRef.current
      );

      if (!activeTabElement) {
        return null;
      }

      const tabLeftEdge = activeTabElement.offsetLeft;
      const tabRightEdge = tabLeftEdge + activeTabElement.offsetWidth;
      const viewportLeftEdge = currentScroll;
      const viewportRightEdge = currentScroll + containerWidth;

      // Tab is cut off on the right - scroll right to show it
      // Add a small buffer (15px) to ensure the tab is fully visible
      if (tabRightEdge > viewportRightEdge) {
        const scrollNeeded = tabRightEdge - containerWidth + 15;
        const finalScroll = Math.min(maxScroll, scrollNeeded);
        return finalScroll;
      }

      // Tab is cut off on the left - scroll left to show it
      if (tabLeftEdge < viewportLeftEdge) {
        return tabLeftEdge;
      }

      return null;
    },
    [activeTab]
  );

  /**
   * Verify scroll transform was applied and clear auto-scroll flag
   */
  const verifyScrollTransform = useCallback((newScrollValue) => {
    const actualElement = tablistElementRef.current;
    if (!actualElement) return;

    const computedTransform = window.getComputedStyle(actualElement).transform;

    // If transform is still 'none', force it directly as a fallback
    if (
      computedTransform === "none" ||
      computedTransform === "matrix(1, 0, 0, 1, 0, 0)"
    ) {
      actualElement.style.transform = `translateX(${-1 * newScrollValue}px)`;
    }

    // Clear the flag after the transition completes (300ms transition + buffer)
    setTimeout(() => {
      autoScrollInProgressRef.current = false;
    }, 400); // 300ms transition + 100ms buffer
  }, []);

  /**
   * Calculate tablist width and ensure active tab is visible
   */
  useEffect(() => {
    const hasValidRefs =
      tablistElementRef.current &&
      tablistContainerRef.current &&
      tablistElementRef.current.children.length > 0;

    if (!hasValidRefs) {
      return;
    }

    // Use requestAnimationFrame with setTimeout to ensure DOM has fully settled after filter changes
    // RAF ensures we wait for next paint, setTimeout ensures layout is complete
    let timeoutId;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        const { totalWidth, containerWidth, maxScroll } = calculateDimensions();
        setTablistWidth(totalWidth);

        // If all tabs fit in viewport, reset scroll to 0
        if (totalWidth <= containerWidth) {
          if (scrollValue !== 0) {
            setScrollValue(0);
          }
          return;
        }

        // If current scroll exceeds maximum (happens when tabs are removed), clamp it
        if (scrollValue > maxScroll) {
          setScrollValue(maxScroll);
          return;
        }

        // Ensure active tab is visible when tab list or selection changes
        if (activeTab === undefined || activeTab === null) {
          return;
        }

        // Check if active tab is outside viewport (e.g., after filter removal)
        const isOutsideViewport = isActiveTabOutsideViewport(
          containerWidth,
          scrollValue
        );

        // Only calculate new scroll if tab is actually outside viewport
        if (isOutsideViewport) {
          const newScrollValue = calculateScrollForActiveTab(
            containerWidth,
            maxScroll,
            scrollValue
          );

          // Update scroll to bring active tab into view
          if (newScrollValue !== null) {
            // Set flag to prevent updateScrollPosition from interfering
            autoScrollInProgressRef.current = true;

            // Update state
            setScrollValue(newScrollValue);

            // Also set the width to ensure re-render
            setTablistWidth(totalWidth);

            // Verify the scroll was applied after state update and clear the flag
            setTimeout(() => verifyScrollTransform(newScrollValue), 150);
          }
        }
      }, 0);
    });

    // Cancelling only the rAF leaked the inner timeout: once the frame had
    // fired, an unmount left the timeout queued and it ran against detached
    // refs. Clear both.
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };

    // scrollValue is intentionally not in deps to avoid infinite loops
    // We only want to recalculate scroll when children or activeTab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    children,
    activeTab,
    calculateDimensions,
    calculateScrollForActiveTab,
    isActiveTabOutsideViewport,
    verifyScrollTransform,
  ]);

  /**
   * Update scroll position when focus changes
   */
  useEffect(() => {
    setScrollIndex(focusedTab);
  }, [focusedTab]);

  /**
   * Hide/show next/prev buttons when necessary
   */
  useEffect(() => {
    updateScrollButtons();
    window.addEventListener("resize", updateScrollButtons);
    return () => {
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [updateScrollButtons]);

  /**
   * Update the scroll position when buttons are hidden/shown
   */
  useEffect(() => {
    updateScrollPosition();
  }, [updateScrollPosition, scrollIndex, showPreviousButton, showNextButton]);

  /**
   * Handle the next button click
   */
  const onNextButtonClick = () => {
    const nextTabIndex = findNextElementOutsideRightEdge();
    if (nextTabIndex > -1) {
      setScrollIndex(nextTabIndex);
    }
  };

  /**
   * Handle the previous button click
   */
  const onPreviousButtonClick = () => {
    const nextTabIndex = findNextElementOutsideLeftEdge();
    if (nextTabIndex > -1) {
      setScrollIndex(nextTabIndex);
    }
  };

  /**
   * Handle keyboard arrow keys
   * @param event
   */
  const onKeyDown = (event) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") {
      nextIndex = Math.min(focusedTab + 1, tabs.length - 1);
    }
    if (event.key === "ArrowLeft") {
      nextIndex = Math.max(focusedTab - 1, 0);
    }
    if (nextIndex !== null) {
      setFocusedTab(nextIndex);
    }
  };

  return (
    <div className={classNames("tablistBar", className)} style={style}>
      {showPreviousButton && (
        <div className={"scrollButton"} aria-hidden="true">
          <IconButton
            type="button"
            className="icon"
            onClick={onPreviousButtonClick}
            tabIndex={-1}
            icon={<SquareLeft />}
          />
        </div>
      )}
      <div className={"tablist"} ref={tablistContainerRef}>
        <ul
          data-testid="tablist-container"
          ref={tablistElementRef}
          onKeyDown={onKeyDown}
          onScroll={updateScrollPosition}
          style={{
            width: `${tablistWidth}px`,
            transform: scrollValue
              ? `translateX(${-1 * scrollValue}px)`
              : undefined,
            transition: "transform 0.3s ease-out",
          }}
        >
          {tabs}
        </ul>
      </div>
      {showNextButton && (
        <div className={"scrollButton"} aria-hidden="true">
          <IconButton
            type="button"
            className="icon"
            onClick={onNextButtonClick}
            tabIndex={-1}
            icon={<SquareRight />}
          />
        </div>
      )}
    </div>
  );
};

TabList.componentName = "TabList";

TabList.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default TabList;
